import { Checkroom, DarkMode, Delete, FitnessCenter, LightMode } from '@mui/icons-material';
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { logger } from '../../utils/logger';
import { clearUserProfile } from '../../utils/userProfile';
import { getCurrentVersion } from '../../utils/versionCheck';

const BottomToolbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showWarning } = useNotification();
  const { mode, toggleTheme } = useTheme();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isVisible, setIsVisible] = useState(true); // Start visible
  const [lastScrollY, setLastScrollY] = useState(0);
  const VERSION = getCurrentVersion();

  // Scroll detection logic
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      // Check if content can be scrolled (has vertical scroll)
      const canScroll = documentHeight > viewportHeight + 10; // 10px threshold

      if (!canScroll) {
        // No vertical scroll - always show toolbar
        setIsVisible(true);
      } else if (currentScrollY === 0) {
        // At the very top - show toolbar
        setIsVisible(true);
      } else if (currentScrollY < lastScrollY) {
        // Scrolling up - show toolbar
        setIsVisible(true);
      } else if (currentScrollY > lastScrollY) {
        // Scrolling down - hide toolbar
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    const handleResize = () => {
      // Re-evaluate on window resize
      handleScroll();
    };

    // Initial check
    handleScroll();

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [lastScrollY]);

  const handleClearLocalStorage = async () => {
    setIsClearing(true);

    try {
      // Clear all localStorage data
      localStorage.clear();

      // Also specifically clear user profile
      clearUserProfile();

      showSuccess('✅ All local data cleared successfully. Reloading application...');

      // Reload the page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      logger.error('Error clearing localStorage', error);
      showWarning('Failed to clear local storage completely');
      setIsClearing(false);
    }
  };

  const handleConfirmClear = () => {
    setIsConfirmDialogOpen(false);
    handleClearLocalStorage();
  };

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          top: 'auto',
          bottom: 0,
          backgroundColor: 'background.paper',
          color: 'text.secondary',
          borderTop: 1,
          borderColor: 'divider',
          boxShadow: '0px -1px 3px rgba(0, 0, 0, 0.12)',
          transform: isVisible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-in-out',
          zIndex: theme => theme.zIndex.appBar,
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: 48,
            justifyContent: 'space-between',
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Logo / Version Number */}
          <Tooltip title="Go to home page" arrow placement="top">
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  color: 'primary.main',
                },
              }}
              onClick={() => navigate('/')}
            >
              PacePilot v{VERSION}
            </Typography>
          </Tooltip>

          {/* Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Browse workout library" arrow placement="top">
              <IconButton
                size="small"
                onClick={() => navigate('/workout-library')}
                aria-label="Browse workout library"
                sx={{
                  color: location.pathname.startsWith('/workout-library')
                    ? 'primary.main'
                    : 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'primary.light',
                  },
                }}
              >
                <FitnessCenter fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Manage custom clothing sets" arrow placement="top">
              <IconButton
                size="small"
                onClick={() => navigate('/clothing-sets')}
                aria-label="Manage custom clothing sets"
                sx={{
                  color: location.pathname === '/clothing-sets' ? 'primary.main' : 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'primary.light',
                  },
                }}
              >
                <Checkroom fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip
              title={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                onClick={toggleTheme}
                aria-label={`Switch to ${mode === 'light' ? 'dark' : 'light'} mode`}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: 'primary.light',
                  },
                }}
              >
                {mode === 'light' ? <DarkMode fontSize="small" /> : <LightMode fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Tooltip
              title="Clear all local storage data (profile, settings, etc.)"
              arrow
              placement="top"
            >
              <IconButton
                size="small"
                onClick={() => setIsConfirmDialogOpen(true)}
                disabled={isClearing}
                aria-label="Clear all local storage data"
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'error.main',
                    backgroundColor: 'error.light',
                  },
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Confirmation Dialog */}
      <Dialog
        open={isConfirmDialogOpen}
        onClose={() => !isClearing && setIsConfirmDialogOpen(false)}
        aria-labelledby="clear-data-dialog-title"
        aria-describedby="clear-data-dialog-description"
      >
        <DialogTitle id="clear-data-dialog-title">Clear All Local Data?</DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-data-dialog-description">
            This will permanently delete all your locally stored data including:
            <br />
            • User profile and preferences
            <br />
            • Training plans and workout history
            <br />
            • Application settings and cache
            <br />
            <br />
            <strong>This action cannot be undone.</strong> The application will reload after
            clearing the data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIsConfirmDialogOpen(false)}
            disabled={isClearing}
            aria-label="Cancel clearing data"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmClear}
            color="error"
            variant="contained"
            disabled={isClearing}
            autoFocus
            aria-label="Confirm clear all data"
          >
            {isClearing ? 'Clearing...' : 'Clear All Data'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BottomToolbar;
